<template>
  <div class="mt-1">
    <b-form>
      <!-- Details Section -->
      <fieldset class="border rounded p-1 mb-1">
        <legend class="w-auto px-50 font-weight-bold field-legend">Details</legend>
        <b-row>
          <!-- Retry Seconds: N, W -->
          <b-col v-if="showRetrySeconds" cols="12" md="6">
            <b-form-group label="Retry Seconds" label-for="retrySeconds">
              <b-form-input
                id="retrySeconds"
                v-model="pathForm.retrySeconds"
                type="number"
                :disabled="isSaving"
                min="0"
              />
            </b-form-group>
          </b-col>

          <!-- Send Comm Trouble -->
          <b-col cols="12" md="6">
            <b-form-group label="Send Comm Trouble" label-for="sendCommunicationTrouble">
              <b-form-select
                id="sendCommunicationTrouble"
                v-model="pathForm.sendCommunicationTrouble"
                :options="yesNoOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>

          <!-- Substitution Code: N, L, W -->
          <b-col v-if="showSubstitutionCode" cols="12" md="6">
            <b-form-group label="Substitution Code" label-for="substitutionCode">
              <b-form-select
                id="substitutionCode"
                v-model="pathForm.substitutionCode"
                :options="substitutionCodeOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>

          <!-- Send Path Info: D, N, L, W -->
          <b-col v-if="showSendPathInfo" cols="12" md="6" class="d-flex align-items-center">
            <b-form-checkbox
              id="sendPathInfo"
              :checked="pathForm.sendPathInfo === 'Y'"
              @change="pathForm.sendPathInfo = $event ? 'Y' : 'N'"
              :disabled="isSaving"
            >
              Send Path Information
            </b-form-checkbox>
          </b-col>

          <!-- Protocol: N, W -->
          <b-col v-if="showProtocol" cols="12" md="6">
            <b-form-group label="Protocol" label-for="protocol">
              <b-form-select
                id="protocol"
                v-model="pathForm.protocol"
                :options="protocolOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>

          <!-- First GPRS APN: L -->
          <b-col v-if="isCellularType" cols="12" md="6">
            <b-form-group label="First GPRS APN" label-for="firstGprsApn">
              <b-form-input
                id="firstGprsApn"
                v-model="pathForm.firstGprsApn"
                type="text"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>

        </b-row>
      </fieldset>

      <!-- DD/CID Details: C, D -->
      <fieldset v-if="showDDCIDAdvanced" class="border rounded p-1 mb-1">
        <legend class="w-auto px-50 font-weight-bold field-legend">DD/CID Details</legend>
        <b-row>
          <b-col cols="12">
            <b-form-checkbox
              id="a893"
              :checked="pathForm['893A'] === 'Y'"
              @change="pathForm['893A'] = $event ? 'Y' : 'N'"
              :disabled="isSaving"
              class="mb-1"
            >
              893 A
            </b-form-checkbox>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Alarm Switchover" label-for="alarmSwitchover">
              <b-form-input
                id="alarmSwitchover"
                v-model="pathForm.alarmSwitchover"
                type="number"
                :disabled="isSaving"
                min="0"
                maxlength="2"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </fieldset>

      <!-- Reports: todos -->
      <fieldset class="border rounded p-1 mb-1">
        <legend class="w-auto px-50 font-weight-bold field-legend">Reports</legend>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Alarm" label-for="alarms">
              <b-form-select
                id="alarms"
                v-model="pathForm.alarms"
                :options="alarmsOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Supv / Trouble" label-for="supervisoryTrouble">
              <b-form-select
                id="supervisoryTrouble"
                v-model="pathForm.supervisoryTrouble"
                :options="alarmsOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="O/C User" label-for="openCloseUser">
              <b-form-select
                id="openCloseUser"
                v-model="pathForm.openCloseUser"
                :options="yesNoOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Door Access" label-for="doorAccess">
              <b-form-select
                id="doorAccess"
                v-model="pathForm.doorAccess"
                :options="doorAccessOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Panic Test" label-for="panicTest">
              <b-form-select
                id="panicTest"
                v-model="pathForm.panicTest"
                :options="yesNoOptions"
                :disabled="isSaving"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </fieldset>
    </b-form>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api';
import {
  ENCRYPTION_SCHEME_OPTIONS,
  ALARMS_OPTIONS,
  DOOR_ACCESS_OPTIONS,
  YES_NO_OPTIONS,
  SUBSTITUTION_CODE_OPTIONS,
  PROTOCOL_OPTIONS,
  showsSendPathInfo,
  showsRetrySeconds,
  showsProtocol,
  showsSubstitutionCode,
  showsDDCIDAdvanced,
  isCellular,
} from './useCommPathConfig';

export default {
  name: 'AdvancedTab',
  props: {
    pathForm: {
      type: Object,
      required: true,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const yesNoOptions = YES_NO_OPTIONS;
    const substitutionCodeOptions = SUBSTITUTION_CODE_OPTIONS;
    const protocolOptions = PROTOCOL_OPTIONS;
    const alarmsOptions = ALARMS_OPTIONS;
    const doorAccessOptions = DOOR_ACCESS_OPTIONS;


    const showSendPathInfo = computed(() => showsSendPathInfo(props.pathForm.communicationType));
    const showRetrySeconds = computed(() => showsRetrySeconds(props.pathForm.communicationType));
    const showProtocol = computed(() => showsProtocol(props.pathForm.communicationType));
    const showSubstitutionCode = computed(() => showsSubstitutionCode(props.pathForm.communicationType));
    const showDDCIDAdvanced = computed(() => showsDDCIDAdvanced(props.pathForm.communicationType));
    const isCellularType = computed(() => isCellular(props.pathForm.communicationType));

    return {
      yesNoOptions,
      substitutionCodeOptions,
      protocolOptions,
      showSendPathInfo,
      showRetrySeconds,
      showProtocol,
      showSubstitutionCode,
      showDDCIDAdvanced,
      alarmsOptions,
      doorAccessOptions,
      isCellularType,
    };
  },
};
</script>

<style scoped lang="scss">
.field-legend {
  font-size: 0.85rem;
  margin-bottom: 0;
  line-height: 1;
}
</style>
